import Cipher from 'aes-ecb'
import { mapActions, mapMutations } from 'vuex'
import { USER_UPDATE_ROUTES, UPDATE_VIEW } from '@/store/types'
import { getToken, getUser, getUserSso } from '@/services/API/authentication'
import { apiGetMenus } from '@/services/API/menus'
import maEventBus from '@/components/eventbus'
import views from '@/components'
import FNOLFlow from '@/fnol'
import * as Settings from '@/infra/settings'

const user = FNOLFlow.isExecuting ? FNOLFlow.params.user : ''

/**
 * @module
 * @name authRoutersBuilder
 * @description Mixin which contains utils used for login purposes.
 */
export default {
  /**
   * @property {String} user.username Username.
   * @property {String} user.password Password.
   * @property {Boolean} sendingLogin Debounce purpose.
   */
  data: function() {
    return {
      user: {
        username: user,
        password: ''
      },
      sendingLogin: false,
      credentials: null
    }
  },

  methods: {
    ...mapActions({
      stateActionSetRoutes: `user/${USER_UPDATE_ROUTES}`
    }),
    ...mapMutations({
      mutUpdateView: UPDATE_VIEW
    }),

    async initAuth() {
      const langFromUrl = Settings.getLangFromUrl()
      if (langFromUrl) {
        Settings.setLocale(langFromUrl)
      }
      const token = Settings.getTokenStorage()
      if (token && !FNOLFlow.isFNOLPlatform) {
        this.setToken(token)
        await this.fetchUser()
        await this.setRoutes()
      } else if (FNOLFlow.isFNOLPlatform) {
        this.sendLogin()
      }
    },

    initI18n: function () {
      const locale = this.matchLocale(Settings.getLocale())

      this.$i18n.locale = locale
      Settings.setLocale(locale)
    },

    matchLocale: function (locale) {
      const locales = this.$i18n.availableLocales

      if (locales.length > 0) {
        return locales.indexOf(locale) !== -1 ? locale : locales[0]
      }

      return null
    },

    /**
     * * Authenticate throw backend API.
     * * Manage response, notifications.
     * * Use localStorage for saving session information.
     * * Manage routes, parse information got from backend and add it to VueRouter.
     * @function
     * @name sendLogin
     */
    async sendLogin(setLocale) {
      this.setCredentials()
      await this.fetchToken(setLocale)
    },

    async fetchToken(setLocale) {
      this.sendingLogin = true

      await getToken(this.credentials)
        .then(async ({ token }) => {
          this.setToken(token)

          await this.fetchUser(setLocale)
          await this.setRoutes()

          if (FNOLFlow.isExecuting) {
            this.initializeFNOLState()
            this.$router.next({ name: 'warranty' })
          }
        })
        .catch(error => {
          if (error.response.status === 403) {
            this.throwNotification(
              this.$t('login.authenticationBlock'),
              '',
              'KO'
            )
          } else {
            this.throwNotification(
              this.$t('login.authenticationError'),
              '',
              'KO'
            )
          }
        })
        .finally(() => {
          this.sendingLogin = false
        })
    },

    async fetchUser(setLocale) {
      await getUser().then(res => {
        this.setUser(res, setLocale)
      })
    },

    setCredentials() {
      if (FNOLFlow.isFNOLPlatform) {
        this.credentials = {
          username: this.FNOLFlow.params.user,
          is_fnol: true
        }
      } else {
        const keyString = 'mavalueisthekey!'
        this.credentials = {
          username: Cipher.encrypt(keyString, this.user.username),
          password: Cipher.encrypt(keyString, this.user.password)
        }
      }
    },

    setToken(token) {
      localStorage.setItem('mavAuth', token)
      sessionStorage.setItem('authCognito', token)
    },

    setUser(user, setLocale) {
      localStorage.setItem('mavRole', user.role)
      if (user.country_code) {
        localStorage.setItem('mavCountryCode', user.country_code)
        if (setLocale) {
          Settings.setLocale(user.country_code && user.lang)
          this.initI18n(user.lang)
        }
      }

      this.$store.commit('user/USER_SET_ROLE', user.role)
      this.$store.commit(
        'user/USER_UPDATE_IS_COMPANYADMIN',
        user.is_company_admin
      )
      this.$store.commit('user/USER_UPDATE_IS_SUPERADMIN', user.is_superadmin)
      this.$store.commit(
        'user/USER_UPDATE_IS_SELFCAREADMIN',
        user.is_selfcare_admin
      )
      this.$store.commit('user/USER_SET_MODE', user.mode)
      this.$store.commit('user/USER_UPDATE_IS_MIX_ENABLE', user.mix_enabled)

      this.setAgenView(user.agent_view)
    },

    setAgenView(agentView) {
      this.mutUpdateView(agentView)
      localStorage.setItem('maAgentView', agentView)
    },

    /**
     * @function
     * @name setRoutes
     * @description Get menus according to the user and / or FNOL filters and
     *              then process it, add it to VueRouter and push to the first of
     *              them.
     */
    setRoutes: async function() {
      let FNOLTabsParams
      if (this.FNOLFlow.isExecuting) {
        FNOLTabsParams = {
          warranty_id: this.FNOLFlow.params.warrantyId,
          warranty_subtype_id: this.FNOLFlow.params.warrantySubtypeId
        }
      }

      await apiGetMenus(FNOLTabsParams)
        .then(data => {
          if (data) {
            this.processRoutes(data)
            this.stateActionSetRoutes(data)
            const deepestChildren = this.getDeepestChildren(data)
            if (deepestChildren.name !== this.$router.currentRoute.name && this.$router.currentRoute.name !== 'extraInfo') {
              this.$router.push({ name: deepestChildren.name })
            }
          }
        })
        .catch(console.error)
    },

    /**
     * @function
     * @name getDeepestChildren
     * @param {Array} nested Array containing route objects.
     * @param {Array} nested.children List of route objects, tree structure.
     * @description Recursive function looking for first deepest child. Nested
     *              could has children property (array of more route objects).
     */
    getDeepestChildren: function(nested) {
      if (Array.isArray(nested)) {
        let foundChild
        for (const item of nested) {
          if (item.hasOwnProperty('children') && item.children.length > 0) {
            foundChild = this.getDeepestChildren(item.children)
          } else {
            foundChild = item
          }
          if (foundChild) break
        }
        return foundChild?.hasOwnProperty('name')
          ? foundChild
          : undefined
      }
    },

    /**
     * @function
     * @name routesComponents
     * @description Function used for recursivity.
     * @see @routeComponent
     * @param {array} routes Array of vue route objects.
     */
    routesComponentes: function(routes) {
      routes.forEach(route => {
        this.routeComponent(route)
      })
    },

    /**
     * @function
     * @name routeComponent
     * @description Replace function which changes name (string from brackend) with
     *              function. Used recursively. 'views' contains all these functions.
     * @see @routesComponents
     * @param {array} route Route object
     */
    routeComponent: function(route) {
      route.component = views[route.component]
      if (route.children && route.children.length > 0) {
        this.routesComponentes(route.children)
      }
    },

    /**
     * @function
     * @name processRoutes
     * @param {Object} data RAW backend array.
     * @description Main function for processing routes.
     * @see parseRoutes
     * @see groupWarrantyRoutes
     * @see routesComponents
     */
    processRoutes: function(data) {
      const routesAlreadyExists = localStorage.getItem('mavRoutes')

      this.parseRoutes(data)
      this.groupWarrantyRoutes(data)
      localStorage.setItem('mavRoutes', JSON.stringify(data))
      this.routesComponentes(data)
      if (!routesAlreadyExists || FNOLFlow.isExecuting) this.$router.addRoutes(data)
    },

    /**
     * @function
     * @name parseRoutes
     * @param {Array} routes RAW backend array.
     * @description Recursve function depending on .childrens.
     */
    parseRoutes: function(routes) {
      routes.forEach(route => {
        this.parseRoute(route)
      })
    },

    /**
     * @function
     * @name parseRoute
     * @param {Object} route RAW backend route object.
     * @description Group metakeys in meta property, set redirect for parents
     *              SIDE_NAV, to redirect to his first children.
     */
    parseRoute: function(route) {
      if (!route) return
      const metaKeys = [
        'type',
        'warrantyid',
        'show_address_info',
        'literal',
        'id',
        'warranty_subtype_id'
      ]
      route.meta = {}

      // meta keys iteration
      metaKeys.forEach(metaKey => {
        // add to meta
        route.meta[metaKey] = route[metaKey]

        // remove from object
        delete route[metaKey]
      })

      // set redirect
      if (route.meta && route.meta.type === 'SIDE_NAV') {
        if (
          route.meta.warrantyid &&
          route.children &&
          route.children.length > 0
        ) {
          route.redirect = { name: route.children[0].name }
          route.meta['redirectRouteName'] = route.children[0].name
        }
      }

      // childrens ? again
      if (route.children && route.children.length > 0) {
        this.parseRoutes(route.children)
      }
    },

    /**
     * @function
     * @name groupWarrantyRoutes
     * @param {Array} routes Array of route objects.
     * @description Any item has warrantyId in his meta, is grouped from warranty.
     */
    groupWarrantyRoutes: function(routes) {
      let warrantyRoutes = routes.filter(route => route.meta.warrantyid)
      let firstChild =
        warrantyRoutes.length > 0 ? { name: warrantyRoutes[0].name } : undefined
      // group warranty
      const groupedRoutes = [
        {
          path: '/warranty',
          name: 'warranty',
          component: 'maSidenavLayout',
          meta: { type: 'LAYOUT', literal: 'layout' },
          redirect: { name: firstChild.name },
          children: [...routes.filter(route => route.meta.warrantyid)]
        },
        ...routes.filter(route => !route.meta.warrantyid)
      ]

      routes.splice(0, routes.length)
      groupedRoutes.forEach(route => {
        routes.push(route)
      })
    }
  },
  /**
     * @function initializeFNOLState
     * @description Inyecta los parámetros de la URL en el módulo 'warranty' de Vuex.
     */
  initializeFNOLState() {
    const prefix = 'warranty/'

    this.$store.commit(`${prefix}WARRANTY_UPDATE_SINISTER_ID`, FNOLFlow.params.refSin)
    this.$store.commit(`${prefix}WARRANTY_UPDATE_ZIP_CODE`, FNOLFlow.params.addressPostcodeSin)
    this.$store.commit(`${prefix}WARRANTY_UPDATE_ADDRESS`, FNOLFlow.params.addressSin)
    this.$store.commit(`${prefix}WARRANTY_UPDATE_PROPERTY_TYPE`, FNOLFlow.params.realEstate)
    this.$store.commit(`${prefix}WARRANTY_UPDATE_RESIDENT_TYPE`, FNOLFlow.params.userType)
    // Conversión de tipos para evitar errores en la API
    this.$store.commit(`${prefix}WARRANTY_UPDATE_MORE_THAN_TWO_YEARS`, FNOLFlow.params.moreThanTwoYears === 'true')
    this.$store.commit(`${prefix}WARRANTY_UPDATE_COORDINATES`, {
      lat: FNOLFlow.params.addressLatSin ? parseFloat(parseFloat(FNOLFlow.params.addressLatSin).toFixed(10)) : null,
      lng: FNOLFlow.params.addressLngSin ? parseFloat(parseFloat(FNOLFlow.params.addressLngSin).toFixed(10)) : null
    })

    // Marcamos el siniestro como "creado" para habilitar las acciones posteriores
    this.$store.commit(`${prefix}WARRANTY_UPDATE_CREATED`, true)
  },
  /**
   * @function
   * @name mounted
   * @description Event sended with the aim of translate the links of the tabnav
  */
  mounted () {
    maEventBus.$on('language:change', async () => {
      await this.setRoutes()
    })
  }
}
